// ComplianceColumns — monthly KPI compliance as a single brand-violet column
// series, the one metric this view is about.
//
// Three deliberate choices:
//   - The y-axis is pinned to 0–100. Compliance is a percentage by
//     definition, and an auto-scaled axis would make 92% and 96% look like
//     opposite ends of the contract's performance.
//   - A month nobody reported in draws NO column, rather than a column of
//     zero height that a reader cannot tell from a genuine 0%. The month keeps
//     its axis tick so the gap is visible as a gap, and the heatmap and the
//     detail table carry a dash for it. A genuine 0% keeps a minimum-height
//     sliver, so the two states never draw the same picture: nobody measured
//     and everybody failed are opposite facts.
//   - The series is brand violet, the palette's "metric the reader is looking
//     at". The outcome colours belong to breakdown charts, where no single
//     series is the subject.
//
// Recharts' accessibility layer is switched OFF deliberately. It wraps the
// plot in a focusable role="application" svg, which inside the labelled image
// is a tab stop that announces nothing; the label plus the tables below are
// the accessible path to the same figures.

import type { JSX } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  PRIMARY_LINE_COLOUR,
  TICK_FONT_SIZE,
  getGridColour,
  getTickColour,
  getTooltipContentStyle,
  useReducedMotionAwareAnimationDuration,
} from "../../../../lib/charts/chartTokens";
import { formatReportMonthShort } from "../reportFormat";
import {
  describeComplianceChart,
  formatComplianceTooltip,
} from "./complianceTooltip";
import type { KpiComplianceValue } from "../../../../types/report";

const CHART_HEIGHT = 240;

/** Minimum drawn height, in pixels, for a month that scored a real nought. */
const MIN_BAR_HEIGHT = 2;

const SERIES_LABEL = "Compliance, weighted across reporting KPIs";

export interface ComplianceTooltipCardProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: KpiComplianceValue }>;
  isDark: boolean;
}

/** Exported so the card can be tested by direct call: Recharts will not
 *  render it under jsdom, and an untested hover card is an untested string. */
export function ComplianceTooltipCard({
  active,
  payload,
  isDark,
}: ComplianceTooltipCardProps): JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;
  const style = getTooltipContentStyle(isDark);
  return (
    <div style={{ ...style, padding: "8px 10px" }}>
      {formatComplianceTooltip(payload[0].payload)}
    </div>
  );
}

export interface ComplianceColumnsProps {
  /** Ascending by month, one entry per month in the window including the
   *  months with no reported result. Never empty: the composer draws one
   *  entry per window position whatever the records say. */
  series: KpiComplianceValue[];
  rangeWeightedPct: number | null;
}

export default function ComplianceColumns({
  series,
  rangeWeightedPct,
}: ComplianceColumnsProps): JSX.Element {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const animationDuration = useReducedMotionAwareAnimationDuration();

  const gridColour = getGridColour(isDark);
  const tickColour = getTickColour(isDark);

  return (
    <div>
      <div
        role="img"
        aria-label={describeComplianceChart(series, rangeWeightedPct)}
        data-testid="kpi-compliance-chart"
        style={{ width: "100%", height: CHART_HEIGHT }}
      >
        <ResponsiveContainer>
          <BarChart
            data={series}
            margin={{ top: 8, right: 16, bottom: 0, left: 4 }}
            accessibilityLayer={false}
          >
            <CartesianGrid
              stroke={gridColour}
              vertical={false}
              strokeDasharray="0"
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: tickColour, fontSize: TICK_FONT_SIZE }}
              tickFormatter={formatReportMonthShort}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: tickColour, fontSize: TICK_FONT_SIZE }}
              width={40}
              domain={[0, 100]}
              tickFormatter={(value: number) => `${value}%`}
            />
            <Tooltip
              content={(props) => (
                <ComplianceTooltipCard
                  {...(props as {
                    active?: boolean;
                    payload?: ReadonlyArray<{ payload: KpiComplianceValue }>;
                  })}
                  isDark={isDark}
                />
              )}
              cursor={{ fill: gridColour }}
            />
            {/* A null `weightedPct` is a gap in Recharts' data, which draws
                no bar at all — exactly the intent. `minPointSize` applies to
                a real value of zero, not to absent data, so the two stay
                distinguishable. */}
            <Bar
              dataKey="weightedPct"
              name={SERIES_LABEL}
              fill={PRIMARY_LINE_COLOUR}
              minPointSize={MIN_BAR_HEIGHT}
              isAnimationActive={animationDuration !== 0}
              animationDuration={animationDuration}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p
        data-testid="kpi-compliance-chart-legend"
        className="mt-2 flex flex-wrap items-center gap-4 text-caption text-slate-600 dark:text-slate-400"
      >
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 rounded-sm"
            style={{ background: PRIMARY_LINE_COLOUR }}
          />
          {SERIES_LABEL}
        </span>
      </p>
    </div>
  );
}
